import React, { useState, useEffect } from "react";
import { Card, Row, Col, Statistic, Spin, Progress, Divider, Button, Table } from "antd";
import { CheckCircleOutlined, CloseCircleOutlined, QuestionCircleOutlined, HistoryOutlined, DownloadOutlined } from "@ant-design/icons";
import { ReportAPI } from "../../../apis/ReportAPI";
import { write, utils } from "xlsx";
import { saveAs } from "file-saver";

function KycDashboard() {
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    yesterday_count: 0,
    today_count: 0,
    today_approved_count: 0,
    today_unapproved_count: 0,
    unchecked_count: 0
  });

  // Total records actioned till date
  const totalRecords = Number(stats.yesterday_count) + Number(stats.today_count);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await ReportAPI.fetchKycStat();
        console.log("KYC Daily Reports:", response.data.reports);
        setStats(response.data.reports[0]);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching KYC stats:", err);
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  // Calculate percentage of approved KYCs for today
  const approvedPercentage = stats.today_count > 0 
    ? Math.round((stats.today_approved_count / stats.today_count) * 100) 
    : 0;

  // Calculate percentage change from yesterday to today
  const percentageChange = stats.yesterday_count > 0
    ? Math.round(((stats.today_count - stats.yesterday_count) / stats.yesterday_count) * 100)
    : 0;
  
  // Determine if volume is up or down compared to yesterday
  const isVolumeUp = stats.today_count > stats.yesterday_count;
  const ApprovedandUnapproved= Number(stats.today_approved_count) + Number(stats.today_unapproved_count);
  // Function to download data as Excel file
  const downloadExcel = () => {
    // Create data structure for the Excel file similar to the PDF format
    const data = [
      {
        "Total records actioned till date": totalRecords,
        "Yesterday count": stats.yesterday_count,
        "Todays count": stats.today_count,
        "Approve and Unapprove count":ApprovedandUnapproved,
        "Remaining Action(Today)": stats.unchecked_count
      }
    ];

    // Create worksheet
    const ws = utils.json_to_sheet(data);
    
    // Set column widths for better readability
    const wscols = [
      { wch: 30 }, // Total records actioned till date
      { wch: 20 }, // Yesterday count
      { wch: 15 }, // Todays count
      { wch: 30 }, // Approve and Unapprove count
      { wch: 25 }  // Remaining Action(Today)
    ];
    ws['!cols'] = wscols;
    
    // Create workbook
    const wb = { 
      Sheets: { "Statistics": ws }, 
      SheetNames: ["Statistics"] 
    };
    
    // Generate Excel file
    const excelBuffer = write(wb, { bookType: "xlsx", type: "array" });
    const fileData = new Blob([excelBuffer], { 
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8" 
    });
    
    // Save file
    saveAs(fileData, `KYC_Stats_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  return (
    <Spin spinning={loading} delay={300}>
      <div className="kyc-dashboard">
        <h1 className="dashboard-title">KYC Daily Summary</h1>
        
        {/* Statistics table with download button */}
        <div className="statistics-table-section" style={{ marginBottom: "24px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
            <h2 style={{ margin: 0 }}>Statistics</h2>
            <Button 
              type="primary" 
              icon={<DownloadOutlined />} 
              onClick={downloadExcel}
            >
              Download Excel
            </Button>
          </div>
          
          <Table 
            columns={[
              {
                title: 'Total records actioned till date',
                dataIndex: 'total',
                key: 'total',
              },
              {
                title: 'Yesterday count',
                dataIndex: 'yesterday',
                key: 'yesterday',
              },
              {
                title: 'Todays count',
                dataIndex: 'today',
                key: 'today',
              },
              {
                title: 'Approve and Unapprove count',
                dataIndex: 'approved',
                key: 'approved',
              },
              {
                title: 'Remaining Action(Today)',
                dataIndex: 'remaining',
                key: 'remaining',
              },
            ]} 
            dataSource={[
              {
                key: '1',
                total: totalRecords,
                yesterday: stats.yesterday_count,
                today: stats.today_count,
                approved: Number(stats.today_approved_count) + Number(stats.today_unapproved_count),
                remaining: stats.unchecked_count,
              }
            ]} 
            pagination={false}
            bordered
            size="middle"
          />
          
          {totalRecords === 0 && <div style={{ padding: "16px", textAlign: "center", color: "#8c8c8c" }}>No data found</div>}
          
          <div style={{ marginTop: "16px" }}>
            <a href="#">Click here to get detailed report</a>
          </div>
        </div>
        
        <Row gutter={[16, 16]}>
          {/* Today's Total KYC Card */}
          <Col xs={24} sm={12} lg={8}>
            <Card className="stat-card">
              <Statistic
                title="Today's Total KYC Processed"
                value={stats.today_count}
                prefix={<HistoryOutlined />}
                suffix={
                  <span className={`change-indicator ${isVolumeUp ? 'positive' : 'negative'}`}>
                    {percentageChange !== 0 ? `${isVolumeUp ? '+' : ''}${percentageChange}%` : 'No change'}
                  </span>
                }
              />
              <div className="comparison-text">
                {`Yesterday: ${stats.yesterday_count}`}
              </div>
            </Card>
          </Col>

          {/* Today's Approved KYC Card */}
          <Col xs={24} sm={12} lg={8}>
            <Card className="stat-card">
              <Statistic
                title="Today's Approved KYC"
                value={stats.today_approved_count}
                valueStyle={{ color: '#3f8600' }}
                prefix={<CheckCircleOutlined />}
              />
              <Progress percent={approvedPercentage} strokeColor="#3f8600" />
              <div className="stat-detail">
                {`${approvedPercentage}% of today's total`}
              </div>
            </Card>
          </Col>

          {/* Today's Unapproved KYC Card */}
          <Col xs={24} sm={12} lg={8}>
            <Card className="stat-card">
              <Statistic
                title="Today's Unapproved KYC"
                value={stats.today_unapproved_count}
                valueStyle={{ color: '#cf1322' }}
                prefix={<CloseCircleOutlined />}
              />
              <Progress 
                percent={stats.today_count > 0 ? Math.round((stats.today_unapproved_count / stats.today_count) * 100) : 0} 
                strokeColor="#cf1322" 
              />
            </Card>
          </Col>

          {/* Unchecked KYC Card */}
          <Col xs={24} sm={12} lg={8}>
            <Card className="stat-card">
              <Statistic
                title="Pending KYC Verifications"
                value={stats.unchecked_count}
                valueStyle={{ color: '#faad14' }}
                prefix={<QuestionCircleOutlined />}
              />
              <div className="stat-detail">
                Awaiting review
              </div>
            </Card>
          </Col>
        </Row>

        <Divider />

        <div className="summary-section">
          <h2>KYC Verification Summary</h2>
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Card>
                <Row gutter={16}>
                  <Col span={8}>
                    <Statistic 
                      title="Total Processed" 
                      value={stats.today_count} 
                      suffix="today" 
                    />
                  </Col>
                  <Col span={8}>
                    <Statistic 
                      title="Approval Rate" 
                      value={`${approvedPercentage}%`} 
                      valueStyle={{ color: approvedPercentage > 80 ? '#3f8600' : '#faad14' }}
                    />
                  </Col>
                  <Col span={8}>
                    <Statistic 
                      title="Volume Change" 
                      value={`${isVolumeUp ? '+' : ''}${percentageChange}%`}
                      valueStyle={{ color: isVolumeUp ? '#3f8600' : '#cf1322' }}
                      suffix="vs yesterday"
                    />
                  </Col>
                </Row>
              </Card>
            </Col>
          </Row>
        </div>
      </div>
    </Spin>
  );
}

export default KycDashboard;